import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from './ui/button';

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'How It Works', path: '/how-it-works' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-[#e2e8f0]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_fresh-renovo/artifacts/q65wxm4z_Screenshot%202026-02-02%20at%203.57.11%E2%80%AFPM.png" 
              alt="RenoNovo Logo" 
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`nav-link text-sm font-medium transition-colors duration-200 ${
                  isActive(link.path)
                    ? 'text-[#2563eb]'
                    : 'text-[#0f172a] hover:text-[#2563eb]'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <a 
              href="tel:+13212102777" 
              className="flex items-center gap-2 text-sm font-medium text-[#64748b] hover:text-[#0f172a] transition-colors duration-200"
              data-testid="phone-link"
            >
              <Phone className="w-4 h-4" />
              <span>(321) 210-2777</span>
            </a>
            <Link to="/contact">
              <Button 
                className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-semibold px-6 h-11 rounded-sm shadow-[0_4px_14px_0_rgba(37,99,235,0.39)] hover:shadow-[0_6px_20px_0_rgba(37,99,235,0.5)] transition-all duration-200 hover:-translate-y-0.5"
                data-testid="header-cta-btn"
              >
                Get Started
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-[#0f172a]"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden absolute left-0 right-0 top-full z-[100] shadow-lg"
          style={{ backgroundColor: '#ffffff' }}
        >
          <nav className="flex flex-col p-6 gap-2 bg-white border-t border-[#e2e8f0]">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`text-lg font-medium py-4 border-b border-[#e2e8f0] ${
                  isActive(link.path) ? 'text-[#2563eb]' : 'text-[#0f172a]'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <div className="pt-4">
              <a 
                href="tel:+13212102777" 
                className="flex items-center gap-2 text-base font-medium text-[#0f172a] py-3"
              >
                <Phone className="w-5 h-5" />
                <span>(321) 210-2777</span>
              </a>
            </div>
            <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
              <Button 
                className="w-full mt-2 bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-semibold h-12 rounded-sm"
                data-testid="mobile-cta-btn"
              >
                Get Started
              </Button>
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};
